import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Award, Shield, Leaf, Server, CheckCircle, Globe, Zap, Target, TrendingUp, Battery } from 'lucide-react'

const certifications = [
  {
    name: 'ISO 9001:2015',
    description: 'Quality Management System',
    icon: Award,
    color: '#6366f1',
  },
  {
    name: 'ISO 14001',
    description: 'Environmental Management',
    icon: Leaf,
    color: '#10b981',
  },
  {
    name: 'ISO 45001',
    description: 'Health & Safety Management',
    icon: Shield,
    color: '#f59e0b',
  },
  {
    name: 'ISO 27001',
    description: 'Information Security',
    icon: Server,
    color: '#ec4899',
  },
  {
    name: 'ISO 20000',
    description: 'IT Service Management',
    icon: Globe,
    color: '#06b6d4',
  },
  {
    name: 'ISO 22000',
    description: 'Food Safety Management',
    icon: Target,
    color: '#84cc16',
  },
  {
    name: 'ISO 22301',
    description: 'Business Continuity',
    icon: Zap,
    color: '#8b5cf6',
  },
  {
    name: 'ISO 26000',
    description: 'Social Responsibility',
    icon: TrendingUp,
    color: '#14b8a6',
  },
  {
    name: 'ISO 31000',
    description: 'Risk Management',
    icon: Shield,
    color: '#f97316',
  },
  {
    name: 'ISO 50001',
    description: 'Energy Management',
    icon: Battery,
    color: '#22c55e',
  },
]

const trustIndicators = [
  { label: 'Quality Assured', description: 'ISO Certified Processes' },
  { label: '15+ Years', description: 'Industry Experience' },
  { label: '100+ Projects', description: 'Successfully Delivered' },
  { label: '50+ Team', description: 'Expert Professionals' },
]

export const CertificationsMarquee = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  return (
    <section ref={ref} className="py-24 relative">
      {/* Background is global in Home.tsx */}
      <div className="container-enterprise relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.3 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
            style={{
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.25)',
              boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
            }}
          >
            <Award className="w-5 h-5 text-primary" />
            <span className="text-sm text-slate-300">Certifications & Standards</span>
          </motion.div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            ISO Certified Excellence
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Internationally recognized certifications ensuring quality, security, and sustainability
          </p>
        </motion.div>

        {/* Certifications Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-8">
          {certifications.map((cert, index) => {
            const Icon = cert.icon
            return (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="group rounded-2xl p-6 text-center transition-all duration-300 hover:translate-y-[-4px]"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: `${cert.color}15`,
                    border: `1px solid ${cert.color}30`,
                  }}
                >
                  <Icon className="w-8 h-8" style={{ color: cert.color }} />
                </div>
                <p className="text-base font-bold text-white mb-1">{cert.name}</p>
                <p className="text-sm text-slate-400">{cert.description}</p>
              </motion.div>
            )
          })}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="rounded-2xl p-6"
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
          }}
        >
          <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8">
            {trustIndicators.map((indicator, index) => (
              <div key={indicator.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{
                    background: 'rgba(16, 185, 129, 0.15)',
                  }}
                >
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{indicator.label}</p>
                  <p className="text-xs text-slate-400">{indicator.description}</p>
                </div>
                {index < trustIndicators.length - 1 && (
                  <div className="hidden sm:block w-px h-10 bg-slate-700 ml-4" />
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}